// WorksheetListPage - List of worksheets with filtering
import {
  PRIORITY_LABEL_KEYS,
  STATUS_LABEL_KEYS,
  TYPE_LABEL_KEYS,
  worksheetBillErrorToastKey,
  WorksheetPriority,
  WorksheetStatus,
} from '@/api/worksheets';
import { Button, Card, Input } from '@/components/ui';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { useWorksheetMutations, useWorksheets, useWorksheetStats } from '@/hooks/use-worksheets';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PendingRequestsTab } from './components/PendingRequestsTab';
import { WorksheetBasketSidebar } from './components/WorksheetBasketSidebar';

const STATUS_COLORS: Record<WorksheetStatus, string> = {
  [WorksheetStatus.FELVEVE]: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
  [WorksheetStatus.FOLYAMATBAN]:
    'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200',
  [WorksheetStatus.VARHATO]:
    'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200',
  [WorksheetStatus.FELFUGGESZTVE]:
    'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400',
  [WorksheetStatus.KESZ]: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200',
  [WorksheetStatus.SZAMLAZANDO]: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-200',
  [WorksheetStatus.LEZART]: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  [WorksheetStatus.TOROLVE]: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200',
};

export function WorksheetListPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorksheetStatus | 'ALL'>('ALL');
  const [machineCategoryFilter, setMachineCategoryFilter] = useState<string>('ALL');
  // page10: csoportos-beadás (intakeBatch) szűrő — a lista-chip kattintásra állítja.
  const [intakeBatchFilter, setIntakeBatchFilter] = useState<string | null>(null);

  // Machine category options for filter
  const MACHINE_CATEGORIES = [
    { code: 'ELECTRIC_SMALL', labelKey: 'worksheet.machineCategory.electricSmall' },
    { code: 'ELECTRIC_LARGE', labelKey: 'worksheet.machineCategory.electricLarge' },
    { code: 'PETROL_HANDHELD', labelKey: 'worksheet.machineCategory.petrolHandheld' },
    { code: 'PETROL_PUSH', labelKey: 'worksheet.machineCategory.petrolPush' },
    { code: 'TRACTOR', labelKey: 'worksheet.machineCategory.tractor' },
    { code: 'BATTERY', labelKey: 'worksheet.machineCategory.battery' },
  ] as const;

  // Server-mode pagination (BE-S2 = A): a lista eddig page/pageSize nélkül hívott
  // → CSAK a BE-default első ~20 munkalapot mutatta (a 21+ némán láthatatlan volt).
  // A WorksheetList gazdag card-lista (nem tábla) → pagination-kontroll a kártyák alá, NEM DataTable.
  const serverPagination = useServerPagination(20);

  // API hooks
  const { worksheets, total, pageSize, isLoading, error, refetch } = useWorksheets({
    search: search || undefined,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    machineCategoryId: machineCategoryFilter !== 'ALL' ? machineCategoryFilter : undefined,
    intakeBatchId: intakeBatchFilter ?? undefined,
    ...serverPagination.query,
  });

  // Szűrő-váltáskor ugorj az 1. oldalra.
  useEffect(() => {
    serverPagination.reset();
  }, [search, statusFilter, machineCategoryFilter, intakeBatchFilter, serverPagination.reset]);

  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const pageIndex = serverPagination.pagination.pageIndex;

  const { stats } = useWorksheetStats();
  const mutations = useWorksheetMutations();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(LOCALE_MAP[locale] ?? 'hu-HU');
  };

  const handleStartWork = async (id: string) => {
    try {
      await mutations.changeStatus(id, WorksheetStatus.FOLYAMATBAN);
      void refetch();
    } catch (err) {
      console.error('Failed to start work:', err);
    }
  };

  // 34-kör R-1 list-view-follow-up: KESZ → „Számla kiállítása" (billing-endpoint, NEM státusz-PATCH —
  // a #698 WorksheetDetailPage-mintát követi). Sikeres billing → invoiceId beáll → KESZ→SZAMLAZANDO derived.
  const handleBill = async (id: string) => {
    try {
      await mutations.billWorksheet(id, { paymentMethod: 'CASH' });
      toast.success(t('service.invoiceSuccess'));
      void refetch();
    } catch (err) {
      toast.error(t(worksheetBillErrorToastKey(err)));
    }
  };

  const handleClose = async (id: string) => {
    try {
      await mutations.changeStatus(id, WorksheetStatus.LEZART);
      void refetch();
    } catch (err) {
      console.error('Failed to close:', err);
    }
  };

  // Calculate stats from hook or fallback
  const displayStats = useMemo(() => {
    if (stats) {
      return stats;
    }
    return {
      total: worksheets.length,
      felveve: worksheets.filter(w => w.status === WorksheetStatus.FELVEVE).length,
      folyamatban: worksheets.filter(w => w.status === WorksheetStatus.FOLYAMATBAN).length,
      varhato: worksheets.filter(w => w.status === WorksheetStatus.VARHATO).length,
      felfuggesztve: worksheets.filter(w => w.status === WorksheetStatus.FELFUGGESZTVE).length,
      kesz: worksheets.filter(w => w.status === WorksheetStatus.KESZ).length,
      szamlazando: worksheets.filter(w => w.status === WorksheetStatus.SZAMLAZANDO).length,
      lezart: worksheets.filter(w => w.status === WorksheetStatus.LEZART).length,
    };
  }, [stats, worksheets]);

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('nav.worksheets')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.subtitle')}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              data-testid="worksheet-batch-intake-button"
              onClick={() => navigate('/worksheet/batch')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10m-8-14v10l8 4"
                />
              </svg>
              {t('worksheet.intakeBatch.title')}
            </Button>

            <Button onClick={() => navigate('/worksheet/new')}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('nav.newWorksheet')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <Card className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.received')}</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {displayStats.felveve}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.inProgress')}</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {displayStats.folyamatban}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.completed')}</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {displayStats.kesz}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.billable')}</p>
            <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
              {displayStats.szamlazando}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 p-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Input
              placeholder={t('service.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1"
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as WorksheetStatus | 'ALL')}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">{t('page.allStatuses')}</option>
              {Object.values(WorksheetStatus).map(value => (
                <option key={value} value={value}>
                  {t(STATUS_LABEL_KEYS[value] ?? value)}
                </option>
              ))}
            </select>
            <select
              value={machineCategoryFilter}
              onChange={e => setMachineCategoryFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">{t('worksheet.filter.allCategories')}</option>
              {MACHINE_CATEGORIES.map(cat => (
                <option key={cat.code} value={cat.code}>
                  {t(cat.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </Card>

        {/* page10: aktív csoportos-beadás (intakeBatch) szűrő jelző + törlés */}
        {intakeBatchFilter && (
          <Card
            data-testid="worksheet-batch-filter-banner"
            className="mb-6 flex items-center justify-between gap-3 border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-800 dark:bg-indigo-900/20"
          >
            <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
              📦 {t('worksheet.intakeBatch.filterActive')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="worksheet-batch-filter-clear"
              onClick={() => setIntakeBatchFilter(null)}
            >
              {t('worksheet.intakeBatch.clearFilter')}
            </Button>
          </Card>
        )}

        {/* Loading state */}
        {isLoading && (
          <Card className="p-4">
            <div className="text-center text-gray-500 dark:text-gray-400">
              {t('service.worksheetLoading')}
            </div>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4">
            <div className="text-center text-red-600 dark:text-red-400">
              {t('page.errorPrefix')}: {error}
            </div>
          </Card>
        )}

        {/* Worksheet list */}
        {!isLoading && !error && (
          <div className="space-y-4">
            {worksheets.map(worksheet => (
              <Card key={worksheet.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-medium text-primary">
                        {worksheet.worksheetNumber}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          STATUS_COLORS[worksheet.status]
                        )}
                      >
                        {t(STATUS_LABEL_KEYS[worksheet.status] ?? worksheet.status)}
                      </span>
                      <span className="rounded-full bg-gray-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {t(TYPE_LABEL_KEYS[worksheet.type] ?? worksheet.type)}
                      </span>
                      {worksheet.priority === WorksheetPriority.SURGOS && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                          {t(PRIORITY_LABEL_KEYS[worksheet.priority] ?? worksheet.priority)}
                        </span>
                      )}
                      {worksheet.intakeBatchId && (
                        <button
                          type="button"
                          data-testid="worksheet-batch-chip"
                          onClick={() => setIntakeBatchFilter(worksheet.intakeBatchId ?? null)}
                          title={t('worksheet.intakeBatch.listBadgeHint')}
                          className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-200 dark:hover:bg-indigo-900"
                        >
                          📦 {t('worksheet.intakeBatch.listBadge')}
                        </button>
                      )}
                    </div>
                    {/* QA-104: a nevet rajzoljuk, ha a BE adja. Optional-guard:
                        nev nelkul a korabbi, csonkolt azonosito marad — a felulet
                        egy pillanatban sem lesz rosszabb a mainal, es ez a valtozas
                        merge-order-fuggetlen a BE-oldali mapper-fixtol.
                        A `||` (nem `??`) az ures nevet is hianyzonak veszi, a
                        `trim()` pedig a csak-szokoz esetet — mindketto MERVE. */}
                    {(() => {
                      const partnerLabel = worksheet.partnerName?.trim();

                      return (
                        <h3
                          className="mt-1 font-medium text-gray-900 dark:text-gray-100"
                          data-testid={
                            partnerLabel
                              ? 'worksheet-list-partner-name'
                              : 'worksheet-list-partner-fallback'
                          }
                        >
                          {t('partner.title')}:{' '}
                          {partnerLabel || `${worksheet.partnerId.substring(0, 8)}...`}
                        </h3>
                      );
                    })()}
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {worksheet.deviceName}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {worksheet.faultDescription}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 dark:text-gray-300">{t('service.reception')}</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatDate(worksheet.receivedAt)}
                      </p>
                    </div>
                    {worksheet.estimatedCompletionDate && (
                      <div>
                        <p className="text-gray-400 dark:text-gray-300">{t('service.deadline')}</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {formatDate(worksheet.estimatedCompletionDate)}
                        </p>
                      </div>
                    )}
                    {worksheet.costLimit && worksheet.costLimit > 0 && (
                      <div>
                        <p className="text-gray-400 dark:text-gray-300">{t('service.limit')}</p>
                        <p className="font-medium text-primary">
                          {worksheet.costLimit.toLocaleString('hu-HU')} {t('common.currency')}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      {worksheet.status === WorksheetStatus.FELVEVE && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStartWork(worksheet.id)}
                          disabled={mutations.isLoading}
                        >
                          {t('service.startWork')}
                        </Button>
                      )}
                      {worksheet.status === WorksheetStatus.KESZ &&
                        worksheet.type !== 'GARANCIALIS' && (
                          <Button
                            size="sm"
                            className="bg-cyan-600 hover:bg-cyan-700"
                            onClick={() => void handleBill(worksheet.id)}
                            disabled={mutations.isLoading}
                          >
                            {t('service.issueInvoice')}
                          </Button>
                        )}
                      {worksheet.status === WorksheetStatus.SZAMLAZANDO && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleClose(worksheet.id)}
                          disabled={mutations.isLoading}
                        >
                          {t('service.close')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/worksheet/${worksheet.id}`)}
                      >
                        {t('common.details')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {worksheets.length === 0 && (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="mt-2">{t('page.noResults')}</p>
              </div>
            )}

            {/* Server-mode pagination kontroll (card-lista — nem DataTable) */}
            {total > pageSize && (
              <div
                data-testid="worksheet-pagination"
                className="flex items-center justify-between border-t border-gray-200 pt-4 text-sm dark:border-gray-700"
              >
                <span className="text-gray-500 dark:text-gray-400">
                  {t('common.total')}: {total}
                </span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageIndex === 0}
                    onClick={() =>
                      serverPagination.setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }))
                    }
                  >
                    {t('common.previous')}
                  </Button>
                  <span className="font-medium text-gray-600 dark:text-gray-300">
                    {pageIndex + 1} / {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageIndex + 1 >= pageCount}
                    onClick={() =>
                      serverPagination.setPagination(p => ({ ...p, pageIndex: p.pageIndex + 1 }))
                    }
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SRV-FG-5-4: Pending public service requests */}
        <div className="mt-8">
          <PendingRequestsTab />
        </div>

        {/* SRV-F2-3: Worksheet Basket Sidebar */}
        <div className="mt-8">
          <WorksheetBasketSidebar />
        </div>
      </main>
    </div>
  );
}
